import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { User } from "lucide-react";

/**
 * Pixel-perfect recreation of the user's reference logo:
 *   • A thick 180° top arc with rounded caps.
 *   • Below it, a solid circle with a circular crescent bite subtracted
 *     from the upper-right (evenodd compound path, exactly per reference).
 *
 * Geometry is inlined verbatim from the provided reference SVG
 * (viewBox 0 0 399 286). `#e1e1e1` is replaced with `currentColor` so the
 * `.stats-icon` rule can theme it for both dark and light modes.
 *
 * DO NOT reinterpret this as an eye/iris/pupil — keep the exact paths.
 */
function InstagramEye({ size = 22, className = "" }) {
  // Preserve the 399:286 aspect ratio of the reference artwork.
  const width = Math.round((size * 399) / 286);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={size}
      viewBox="0 0 399 286"
      className={className}
      aria-hidden
    >
      {/* Solid circle with a crescent circular subtraction (evenodd) */}
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M199.523,74.644A68.4,68.4,0,0,1,207,75c-22.938,27.5-20.958,68.217,4,93,24.275,24.1,63.587,26.475,91,5,0.059,2.375.02,4.717-.119,7.077-2.514,42.647-38.794,105.433-102.358,105.433-56.531,0-102.358-47.2-102.358-105.433C97.165,116.474,156.363,74.962,199.523,74.644Z"
      />
      {/* Thick 180° top arc, rounded caps */}
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="27"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14,134a203.376,203.376,0,0,1,371,0"
      />
    </svg>
  );
}

/**
 * Animated hero stats band.
 *
 * • IntersectionObserver fires ONCE at ~30% visibility.
 * • Counters count up via requestAnimationFrame with an ease-out curve.
 * • Whole row also fades + translates in via Framer Motion.
 * • Honors `prefers-reduced-motion` (renders final values immediately).
 *
 * Icons and formatting are kept identical to the reference brief — do not
 * substitute Eye/UserRound with other icons.
 */
const STATS = [
  { key: "views",     target: 1.4e9,  format: formatViews,  label: "Views delivered",     icon: "eye" },
  { key: "followers", target: 26e6,   format: formatBigInt, label: "Followers collected", icon: "user" },
  { key: "clicks",    target: 102.3,  format: formatMult,   label: "Highest CTR",         icon: "pill" },
  { key: "revenue",   target: 2.7e6,  format: formatDollar, label: "Revenue collected",   icon: null },
];

function formatViews(v) {
  // Views target is 1.4B — render as "1.4B+" once above 1B, with sensible
  // intermediate steps for the count-up animation.
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B+`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(0)}M`;
  if (v >= 1000) return `${Math.round(v / 1000)}K`;
  return String(Math.round(v));
}
function formatBigInt(v) {
  // Followers → "26M" style. Below a million falls back to K / plain int
  // so the count-up animation ticks through cleanly.
  if (v >= 1e6) return `${(v / 1e6).toFixed(0)}M`;
  if (v >= 1000) return `${Math.round(v / 1000)}K`;
  return String(Math.round(v));
}
function formatMult(v)   { return `${v.toFixed(1)}x`; }
function formatDollar(v) {
  // Revenue → "$2.7M" style.
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1000) return `$${Math.round(v / 1000)}K`;
  return `$${Math.round(v)}`;
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function useCountUp(target, active, duration = 1400) {
  const [value, setValue] = useState(0);
  const rafRef = useRef();
  const startTs = useRef(null);

  useEffect(() => {
    if (!active) return;
    startTs.current = null;
    const tick = (ts) => {
      if (startTs.current === null) startTs.current = ts;
      const elapsed = ts - startTs.current;
      const p = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(p);
      setValue(target * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, target, duration]);
  return value;
}

function StatItem({ stat, active, reduced }) {
  const v = useCountUp(stat.target, active && !reduced);
  const display = reduced || !active ? stat.format(stat.target) : stat.format(v);
  const isDollar = stat.icon === null;
  const isPill = stat.icon === "pill";

  return (
    <div className="flex flex-col items-center gap-2" data-testid={`stat-${stat.key}`}>
      <div className="flex items-center gap-2 stats-glass">
        {stat.icon === "eye" && (
          <InstagramEye size={22} className="stats-icon" />
        )}
        {stat.icon === "user" && (
          // Sized to visually match the InstagramEye icon (~30px wide, 22px tall).
          <User size={28} strokeWidth={2} className="stats-icon" />
        )}
        {isPill ? (
          <span
            className="stats-pill inline-flex items-center px-3 py-0.5 rounded-full border text-3xl md:text-4xl font-bold tracking-tight"
          >
            {display}
          </span>
        ) : (
          <span className="text-3xl md:text-4xl font-bold tracking-tight tabular-nums">
            {display}
          </span>
        )}
      </div>
      <div className="text-xs uppercase tracking-wider stats-label">{stat.label}</div>
    </div>
  );
}

export default function StatsBand() {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const reduced = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.3) {
            setActive(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: [0.3] },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="stats-band relative">
      <motion.div
        initial={reduced ? undefined : { opacity: 0, y: 16 }}
        animate={active ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-7xl mx-auto px-6 sm:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center"
      >
        {STATS.map((s) => (
          <StatItem key={s.key} stat={s} active={active} reduced={!!reduced} />
        ))}
      </motion.div>
    </section>
  );
}
